import { DateTimeFormatter } from "components/DateTimeFormatter/DateTimeFormatter";
import { CustomLinkMarkdown } from "components/Form/components/Markdown/Markdown";
import PageContent from "components/Layout/PageContent";
import RecordLabel from "components/RecordLabel";
import { RuntimeProgressBar } from "components/RuntimeProgressBar/RuntimeProgressBar";
import StatusBadge from "components/StatusBadge";
import { ExecutionStatusBadge } from "components/StatusBadge/ExecutionStatusBadge";
import { TimeAgo } from "components/TimeAgo/TimeAgo";
import settingConfig from "config/resources/setting";
import { getExecutionMode, hasExecutionEndedStatus } from "config/ts-utils";
import { executionSubscription } from "containers/Executions/Executions.type";
import { DependencyVisualization } from "containers/Resources/screens/ResourcesSingle/components/Visualization/DependencyVisualization/DependencyVisualization";
import {
    subscribeUpdates,
    useReconnectingWebsocket,
    useSecondaryLiveDataHold,
} from "containers/Resources/screens/ResourcesSingle/utils";
import { Execution } from "graphql/generated/graphql";
import qs from "qs";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useRouteMatch } from "react-router-dom";
import { getRelativeUrl } from "utils/service";
import "./ExecutionSingleStatus.scss";

interface ExecutionSingleStatusProps {
    data: Execution;
    showErrorScreen: () => void;
}

export const ExecutionSingleStatus = ({ data, showErrorScreen }: ExecutionSingleStatusProps) => {
    const dispatch = useDispatch();
    const match: any = useRouteMatch("/:recordName");
    const queryString = qs.parse(window?.location?.search.substring(1));
    const recordName = match?.params?.recordName;
    const recordId = queryString?.id;
    const iri = `/${recordName}/${recordId}`;
    const typedWindow: any = window;
    const visualizationEnabled = typedWindow.REACT_APP_GRAPH_VISUALIZATION_ENABLED ?? true;

    const secondaryLiveDataHold = useSecondaryLiveDataHold();
    const { getSocket, deleteSocket } = useReconnectingWebsocket();

    useEffect(() => {
        if (!data?.id || secondaryLiveDataHold) {
            return undefined;
        }

        return subscribeUpdates({
            subscription: executionSubscription,
            recordId,
            dispatch,
            recordName,
            iri,
            showErrorScreen,
            getSocket,
            deleteSocket,
        });
    }, [secondaryLiveDataHold, data?.id, deleteSocket, dispatch, getSocket, iri, recordId, recordName, showErrorScreen]);

    return (
        <PageContent className="execution-single-status">
            {visualizationEnabled && data && (
                <DependencyVisualization hasParent={data?.parent_execution_id != null} />
            )}
            <div className="execution-single-status__statuses">
                {getExecutionMode(data) ? (
                    <StatusBadge
                        darkText
                        size="large"
                        text={getExecutionMode(data)}
                        type="primary-yellow"
                    />
                ) : null}
                <ExecutionStatusBadge data={data} />
                {data?.setting_id && (
                    <RecordLabel
                        link={getRelativeUrl("setting", data?.setting_id)}
                        name={data?.setting_id_setting?.name}
                        icon={settingConfig.icon}
                    />
                )}
                {data?.scheduled_for && data?.status !== "PAUSED" && (
                    <span>
                        {data?.status === "SCHEDULED" ? "for" : "until"}{" "}
                        <DateTimeFormatter timestamp={data?.scheduled_for} />
                    </span>
                )}
                {data?.lineno && (
                    <span>
                        Line {data.lineno} {data.last_command}
                    </span>
                )}
            </div>
            {data?.id && data?.started_at && !hasExecutionEndedStatus(data.status as any) && (
                <div style={{ marginTop: 8 }}>
                    {data.status === "AT_ENGINE" && (
                        <div style={{ marginBottom: 4, opacity: 0.7, fontSize: "0.9em" }}>
                            running since <TimeAgo datetime={data.started_at} />
                        </div>
                    )}
                    <RuntimeProgressBar executionId={data.id} startedAt={data.started_at} />
                </div>
            )}
            <CustomLinkMarkdown
                markdownString={data?.message ?? ""}
                className="executionMessage statusMessageMarkdown"
            />
        </PageContent>
    );
};
